import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Log de diagnóstico do fluxo de débito, gravado no Firestore.
 *
 * Objetivo: enxergar em produção onde o débito para, já que o cliente relata
 * "escaneei e não aconteceu nada" sem mensagem de erro na tela.
 *
 * Regras de ouro deste módulo:
 * - NUNCA lançar. Uma falha de log não pode derrubar o pagamento.
 * - NUNCA bloquear. Todas as chamadas são disparadas sem await no caminho feliz.
 * - Números vão gravados também como string, para preservar a precisão real
 *   (117.94999999999999 aparece como 117.95 em qualquer visualizador que arredonde).
 */

const COLECAO_LOGS = 'logs_debito';

/**
 * Disjuntor: se as regras do Firestore não permitirem escrita em
 * `logs_debito`, toda chamada falharia — uma por evento, para sempre.
 * Depois de algumas falhas seguidas o log se desliga sozinho, para não
 * gerar tráfego nem ruído de console no fluxo de pagamento.
 */
const MAX_FALHAS_SEGUIDAS = 3;
let falhasSeguidas = 0;
let desligado = false;

/** Identifica todos os eventos de uma mesma tentativa de pagamento. */
let sessaoAtual: string | null = null;

export function iniciarSessaoLog(paymentIntentId: string): void {
  sessaoAtual = paymentIntentId;
}

export function encerrarSessaoLog(): void {
  sessaoAtual = null;
}

/**
 * Converte um valor qualquer em algo que o Firestore aceite.
 * O Firestore rejeita `undefined` e estoura em referências circulares.
 */
function sanitizar(valor: unknown, profundidade = 0): unknown {
  if (valor === undefined) return null;
  if (valor === null) return null;
  if (profundidade > 6) return '[profundo demais]';

  const tipo = typeof valor;

  if (tipo === 'number') {
    const n = valor as number;
    return Number.isFinite(n) ? n : String(n);
  }
  if (tipo === 'string' || tipo === 'boolean') return valor;
  if (tipo === 'function' || tipo === 'symbol') return `[${tipo}]`;

  if (valor instanceof Error) {
    return {
      nome: valor.name,
      mensagem: valor.message,
      codigo: (valor as unknown as { code?: string }).code ?? null,
    };
  }
  if (valor instanceof Timestamp) return valor;
  if (valor instanceof Date) return valor.toISOString();

  if (Array.isArray(valor)) {
    return valor.slice(0, 50).map((item) => sanitizar(item, profundidade + 1));
  }

  if (tipo === 'object') {
    const saida: Record<string, unknown> = {};
    for (const [chave, item] of Object.entries(valor as Record<string, unknown>)) {
      saida[chave] = sanitizar(item, profundidade + 1);
    }
    return saida;
  }

  return String(valor);
}

/**
 * Grava um evento de diagnóstico.
 *
 * @param evento Nome do ponto do fluxo, ex.: 'cliente.saldo_avaliado'.
 * @param dados  Contexto livre. Use `num()` para qualquer valor monetário.
 */
export function logDebito(evento: string, dados: Record<string, unknown> = {}): void {
  if (desligado) return;

  // Dispara sem await: o log nunca entra no caminho crítico do pagamento.
  // O SDK do Firestore entrega a escrita mesmo sem ninguém aguardar.
  void (async () => {
    try {
      await addDoc(collection(db, COLECAO_LOGS), {
        evento,
        sessao: sessaoAtual,
        dados: sanitizar(dados),
        criado_em: Timestamp.now(),
        ts_dispositivo: new Date().toISOString(),
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        url: typeof window !== 'undefined' ? window.location.href : null,
      });
      falhasSeguidas = 0;
    } catch (erro) {
      falhasSeguidas++;
      console.error(`[logDebito] falhou ao gravar '${evento}':`, erro);
      if (falhasSeguidas >= MAX_FALHAS_SEGUIDAS) {
        desligado = true;
        console.error(
          `[logDebito] desligado após ${MAX_FALHAS_SEGUIDAS} falhas seguidas. ` +
          `Verifique a regra de 'create' na coleção '${COLECAO_LOGS}'.`
        );
      }
    }
  })();
}

/** Alias mantido para leitura: toda chamada já é não-bloqueante. */
export const logDebitoAsync = logDebito;

/**
 * Empacota um número com sua representação exata em string.
 * `num(117.94999999999999)` => { v: 117.94999999999999, s: '117.94999999999999' }
 */
export function num(valor: unknown): { v: number | null; s: string } {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) {
    return { v: null, s: String(valor) };
  }
  return { v: valor, s: valor.toString() };
}
